package pl.decks.personal.pages.ievp.sections.skills

import pl.decks.personal.components.widgets.ranks.Rank

enum class SkillCategory(
    val title: String,
    val rankLevel: Rank.Slot,
    val skills: List<Skill>,
) {
    SYSTEM_INTEGRATION(
        "System integration", Rank.Slot.S4, listOf(
            Skill("REST API", 68),
            Skill("GraphQL", 37),
            Skill("Queues & Brokers", 43),
            Skill("API gateways", 35),
        )
    ),
    DISTRIBUTED_DESIGNS(
        "Distributed designs", Rank.Slot.S6, listOf(
            Skill("Scalability", 72),
            Skill("Serverless", 62),
            Skill("Microservices", 58),
            Skill("Event-driven", 53),
        )
    ),
    CLOUD_INFRASTRUCTURE(
        "Cloud infrastructure", Rank.Slot.S5, listOf(
            Skill("AWS", 76),
            Skill("GCP", 54),
            Skill("Azure", 18),
            Skill("Infrastructure as Code", 43),
        )
    ),
    SECURITY_COMPLIANCE(
        "Security & Compliance", Rank.Slot.S6, listOf(
            Skill("Data regulations (GDPR, HIPAA)", 78),
            Skill("Threat modelling", 68),
            Skill("Identity (OAuth, SSO)", 55),
            Skill("Zero trust", 52),
        )
    ),

    SYSTEM_ADMINISTRATION(
        "System administration", Rank.Slot.S6, listOf(
            Skill("Environment management", 67),
            Skill("Linux", 72),
            Skill("Bash", 68),
            Skill("Containerization (Docker, K8s)", 55),
        )
    ),
    FULL_STACK(
        "Full-stack coverage", Rank.Slot.S6, listOf(
            Skill("BE", 76),
            Skill("FE", 58),
            Skill("Multiplatform", 64),
            Skill("API Development", 54),
        )
    ),
    LANGUAGES_DEPTH(
        "Language depth", Rank.Slot.S7, listOf(
            Skill("Python", 84),
            Skill("Kotlin", 74),
            Skill("JS/TS", 55),
            Skill("R", 46),
        )
    ),
    PRACTICES(
        "Practices", Rank.Slot.S8, listOf(
            Skill("Code review", 87),
            Skill("CI/CD", 74),
            Skill("Design Patterns", 72),
            Skill("Git", 77),
        )
    ),

    FOUNDATIONS_GOVERNANCE(
        "Foundations & Governance", Rank.Slot.S7, listOf(
            Skill("Data modelling", 83),
            Skill("Data quality", 64),
            Skill("Schema registry", 72),
            Skill("Schema versioning", 63),
            Skill("dbt", 50),
        )
    ),
    PIPELINES_ORCHESTRATION(
        "Pipelines & Orchestration", Rank.Slot.S7, listOf(
            Skill("Cluster management", 73),
            Skill("Monitoring", 72),
            Skill("Lineage", 65),
            Skill("Airflow", 74),
            Skill("Kafka", 43),
        )
    ),
    STORAGE_PROCESSING(
        "Storage & Processing", Rank.Slot.S8, listOf(
            Skill("DataBricks", 88),
            Skill("Snowflake", 72),
            Skill("Spark", 84),
            Skill("SQL", 72),
            Skill("NoSQL", 64),
        )
    ),
    DATA_ARCHITECTURES(
        "Data architectures", Rank.Slot.S8, listOf(
            Skill("Lake", 88),
            Skill("Warehouse", 83),
            Skill("Lambda/Kappa", 68),
            Skill("Mesh", 54),
            Skill("Streaming", 47),
        )
    ),

    MODEL_BUILDING(
        "Model building", Rank.Slot.S2, listOf(
            Skill("Feature Stores", 43),
            Skill("Hyperparameter tuning", 42),
            Skill("scikit", 48),
            Skill("MLFlow", 28),
            Skill("PyTorch", 14),
        )
    ),
    KNOWLEDGE_FOUNDATION(
        "Knowledge foundation", Rank.Slot.S6, listOf(
            Skill("Probabilistic modelling", 72),
            Skill("Time series", 67),
            Skill("NLP", 66),
            Skill("Anomaly detection", 59),
            Skill("LLMs", 51),
        )
    ),
    ANALYTICS_VISUALS(
        "Visual data-telling", Rank.Slot.S8, listOf(
            Skill("Typography", 82),
            Skill("Graphs", 78),
            Skill("Dashboards", 72),
            Skill("Reports", 63),
            Skill("Notebooks", 58),
        )
    ),
    RESEARCH_EXPERIMENTATION(
        "Research & Experimentation", Rank.Slot.S8, listOf(
            Skill("Hypothesis formulation", 84),
            Skill("Experiment design", 80),
            Skill("Results evaluation", 68),
            Skill("Monte Carlo", 80),
            Skill("A/B testing", 73),
        )
    ),

    DESIGN_UX(
        "Design, UX & UI", Rank.Slot.S5, listOf(
            Skill("Wireframing", 70),
            Skill("UX Research", 72),
            Skill("Figma", 34),
            Skill("Design systems", 43),
            Skill("Requirements gathering", 85),
        )
    ),
    GO_TO_MARKET(
        "Go-to-market", Rank.Slot.S7, listOf(
            Skill("Business validation", 85),
            Skill("Business development", 72),
            Skill("Business model design", 71),
            Skill("Client relationships", 54),
            Skill("Sales", 67),
        )
    ),
    CUSTOMER_DISCOVERY(
        "Customer & Discovery", Rank.Slot.S6, listOf(
            Skill("User interviews", 70),
            Skill("Feedback synthesis", 66),
            Skill("Segmentation", 63),
            Skill("Cohort analysis", 60),
            Skill("Competitive benchmarking", 64),
        )
    ),
    PRODUCT_DEVELOPMENT(
        "Product development", Rank.Slot.S9, listOf(
            Skill("Roadmap formulation", 72),
            Skill("Prototyping", 90),
            Skill("MVP scoping", 79),
            Skill("PMF validation", 84),
            Skill("Product scaling", 74),
        )
    ),

    COLLABORATION(
        "Collaboration", Rank.Slot.S9, listOf(
            Skill("Asynchronous communication", 90),
            Skill("Cross-team alignment", 83),
            Skill("Conflict resolution", 74),
            Skill("Delivery rituals", 80),
        )
    ),
    TEAM_CRAFT(
        "Team craft", Rank.Slot.S8, listOf(
            Skill("Culture shaping", 85),
            Skill("Mentoring", 74),
            Skill("Skill assessment", 67),
            Skill("Role versatility", 88),
        )
    ),
    STRATEGIC_COMMUNICATION(
        "Strategic communication", Rank.Slot.S7, listOf(
            Skill("Storytelling", 87),
            Skill("Technical writing", 78),
            Skill("Speeches", 60),
            Skill("Stakeholder alignment", 72),
        )
    ),
    CHANGE_MANAGEMENT(
        "Change management", Rank.Slot.S8, listOf(
            Skill("Adoption strategy", 80),
            Skill("Process transition", 72),
            Skill("Knowledge transfer", 84),
            Skill("Innovation management", 80),
        )
    ),
    ;

    data class Skill(
        val name: String,
        val ranking: Int,
    )
}
